import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faCircleCheck,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useSelector } from "react-redux";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getPublicToken } from "../../../services/PublicTokenService";

function canCreatePublicLink({
  role,
  hasSubscription,
  userId,
  athleteProfileId,
}) {
  if (role === "admin" || role === "highschool" || role === "university") {
    return true;
  }
  if (
    role === "athlete" &&
    hasSubscription &&
    String(userId) === String(athleteProfileId)
  ) {
    return true;
  }
  return false;
}

export default function CopyUrl({ athleteProfileId }) {
  const authUser = useAuthUser();
  const apiInstance = useAxiosInstance();
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const role = authUser?.user?.role;
  const userId = authUser?.user?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch (error) {
      console.error("Failed to copy!", error);
      return false;
    }
  };

  const allowed = canCreatePublicLink({
    role,
    hasSubscription,
    userId,
    athleteProfileId,
  });

  if (!allowed) return null;

  const handleCopy = async () => {
    if (isLoading || copied) return;
    setIsLoading(true);
    try {
      const data = await getPublicToken(apiInstance, athleteProfileId);
      const token = data?.token ?? data;
      const publicUrl = `${window.location.origin}/public-link/${token}`;
      await copyToClipboard(publicUrl);
    } catch (error) {
      console.error("Failed to generate public link:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Preserved private link logic (may be re-enabled if both link types are needed):
  // const profileBaseRole =
  //   authUser?.user?.role === "highschool" ? "highschool-coach" : authUser?.user?.role;
  // const privateUrl = `${process.env.REACT_APP_URL}/${profileBaseRole}/athlete-profile/${athleteProfileId}`;

  return (
    <button
      onClick={handleCopy}
      disabled={isLoading}
      className="relative group px-1.5 rounded-full hover:bg-gray-100 transition disabled:opacity-60"
    >
      {isLoading ? (
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className="w-4 h-4 text-gray-400"
        />
      ) : copied ? (
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="w-4 h-4 text-green-600"
        />
      ) : (
        <FontAwesomeIcon icon={faLink} className="w-4 h-4 text-gray-500" />
      )}
      <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs font-normal rounded bg-black text-white opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
        {copied ? "Copied!" : "Copy link to Athlete"}
      </span>
    </button>
  );
}
